import React, { useEffect, useMemo, useState } from "react";
import { Form, Input, InputNumber, Select, Switch, Row, Col, Typography, Divider } from "antd";

import { detailFieldsMapping, historyTypesOptions } from "../../../../../types/healthcare/MedicalHistoryTypes";
import { fetchTestCategories } from "../../../../../api/healthcare/Testing/TestCategories";
import { fetchTestTypesByCategory } from "../../../../../api/healthcare/Testing/TestTypes";
import { fetchCustomTestPackages } from "../../../../../api/healthcare/Testing/CustomTestPackageApi";
import { TestCategory, TestType, CustomTestPackage } from "../../../../../types/healthcare/TestTypes";

const { TextArea } = Input;
const { Text } = Typography;

interface ScratchpadDraftFormProps {
	section: string;
	value: Record<string, any>;
	onChange: (next: Record<string, any>) => void;
}

/** Immutable set of a (possibly nested) path on a plain object. */
const setAt = (obj: Record<string, any>, path: (string | number)[], val: any): Record<string, any> => {
	const next: Record<string, any> = Array.isArray(obj) ? [...obj] : { ...obj };
	let cursor: any = next;
	for (let i = 0; i < path.length - 1; i++) {
		cursor[path[i]] = { ...(cursor[path[i]] || {}) };
		cursor = cursor[path[i]];
	}
	cursor[path[path.length - 1]] = val;
	return next;
};

const prettify = (key: string): string => key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Constrained editor for a proposed `test_order` draft. Feeds Category / Test Type / Custom Package
 * selects from the real per-deployment catalogue (mirrors CreateTestOrderModal). Split into its own
 * component so the data-fetching hooks only run for the test_order section (avoids conditional hooks
 * in the parent). Fetch failures fall back to empty options quietly, matching the modal.
 */
const TestOrderDraftFields: React.FC<{
	value: Record<string, any>;
	onChange: (next: Record<string, any>) => void;
}> = ({ value, onChange }) => {
	const [categories, setCategories] = useState<TestCategory[]>([]);
	const [testTypes, setTestTypes] = useState<TestType[]>([]);
	const [packages, setPackages] = useState<CustomTestPackage[]>([]);

	const selectedTestTypes: string[] = Array.isArray(value.test_type) ? (value.test_type as string[]) : [];
	const selectedPackages: string[] = Array.isArray(value.custom_test_packages) ? (value.custom_test_packages as string[]) : [];

	const loadTypesForCategory = async (categoryValue: string) => {
		if (!categoryValue) {
			setTestTypes([]);
			return;
		}
		try {
			const response = await fetchTestTypesByCategory(categoryValue);
			if (response.success) {
				setTestTypes(response.data.filter((t) => t.is_active));
			}
		} catch {
			// No types / fetch failure is a normal empty state — surface no toast (matches the modal).
			setTestTypes([]);
		}
	};

	// Load categories + packages once, and — when the AI has pre-picked a category — the saved
	// category's types so the saved test_type slugs render with labels (mirrors the modal's
	// existing-order effect). Runs on mount only.
	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const response = await fetchTestCategories({ include_test_types: false });
				if (!cancelled && response.success) {
					setCategories(response.data.filter((c) => c.is_active));
				}
			} catch {
				if (!cancelled) setCategories([]);
			}
		})();
		(async () => {
			try {
				const pkgs = await fetchCustomTestPackages({ active_only: true, include_analysis_types: true });
				if (!cancelled) setPackages(pkgs);
			} catch {
				if (!cancelled) setPackages([]);
			}
		})();
		if (value.test_category) {
			loadTypesForCategory(value.test_category as string);
		}
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// User picks a category directly → reset test_type and load that category's types.
	const handleCategoryChange = (categoryValue: string) => {
		onChange({ ...value, test_category: categoryValue, test_type: [] });
		loadTypesForCategory(categoryValue);
	};

	// User picks custom packages → derive test_type (union of the packages' analysis_types) and
	// test_category (first package's category), then load that category's types (mirrors
	// CreateTestOrderModal.handlePackageChange).
	const handlePackageChange = (values: string[]) => {
		const chosen = packages.filter((pkg) => values.includes(pkg.value));
		const combinedTestTypes = chosen.flatMap((pkg) => pkg.analysis_types || []);
		const uniqueTestTypes = [...new Set(combinedTestTypes)];
		const primaryCategory = chosen[0]?.analysis_type_details?.[0]?.test_category?.value?.toString() || "";

		onChange({
			...value,
			custom_test_packages: values,
			test_type: uniqueTestTypes,
			test_category: primaryCategory,
		});
		if (primaryCategory) {
			loadTypesForCategory(primaryCategory);
		}
	};

	const categoryOptions = categories.map((c) => ({ value: c.value, label: c.name }));
	const packageOptions = packages.map((p) => ({ value: p.value, label: p.name }));

	// Keep any already-selected test_type slugs as options even if they aren't in the loaded list,
	// so a saved selection never silently disappears.
	const testTypeOptions = useMemo(() => {
		const opts = testTypes.map((t) => ({ value: t.value, label: t.name }));
		const known = new Set(opts.map((o) => o.value));
		selectedTestTypes.forEach((slug) => {
			if (!known.has(slug)) opts.push({ value: slug, label: slug });
		});
		return opts;
	}, [testTypes, selectedTestTypes]);

	return (
		<Form layout="vertical">
			<Form.Item label="Test Category" required>
				<Select
					data-testid="draft-test_category"
					showSearch
					optionFilterProp="label"
					style={{ width: "100%" }}
					value={(value.test_category as string) || undefined}
					options={categoryOptions}
					placeholder="Select test category"
					onChange={handleCategoryChange}
				/>
			</Form.Item>
			<Form.Item label="Test Types" required>
				<Select
					data-testid="draft-test_type"
					mode="multiple"
					showSearch
					optionFilterProp="label"
					style={{ width: "100%" }}
					value={selectedTestTypes}
					options={testTypeOptions}
					disabled={!value.test_category}
					placeholder={value.test_category ? "Select test types" : "Select a category first"}
					onChange={(v: string[]) => onChange({ ...value, test_type: v })}
				/>
			</Form.Item>
			<Form.Item label="Custom Packages">
				<Select
					data-testid="draft-custom_test_packages"
					mode="multiple"
					showSearch
					optionFilterProp="label"
					style={{ width: "100%" }}
					value={selectedPackages}
					options={packageOptions}
					placeholder="Select custom test packages"
					onChange={handlePackageChange}
				/>
			</Form.Item>
			<Row gutter={[16, 8]}>
				<Col xs={24} md={8}>
					<Form.Item label="Priority">
						<Select
							data-testid="draft-priority"
							style={{ width: "100%" }}
							value={(value.priority as string) ?? "routine"}
							onChange={(v: string) => onChange({ ...value, priority: v })}
							options={[
								{ value: "routine", label: "Routine" },
								{ value: "urgent", label: "Urgent" },
								{ value: "stat", label: "Stat" },
							]}
						/>
					</Form.Item>
				</Col>
				<Col xs={24} md={8}>
					<Form.Item label="Status">
						<Select
							data-testid="draft-status"
							style={{ width: "100%" }}
							value={(value.status as string) ?? "ordered"}
							onChange={(v: string) => onChange({ ...value, status: v })}
							options={[
								{ value: "ordered", label: "Ordered" },
								{ value: "scheduled", label: "Scheduled" },
							]}
						/>
					</Form.Item>
				</Col>
				<Col xs={24} md={8}>
					<Form.Item label="Test Date">
						<Input
							data-testid="draft-test_date"
							placeholder="YYYY-MM-DD"
							value={(value.test_date as string) ?? ""}
							onChange={(e) => onChange({ ...value, test_date: e.target.value || null })}
						/>
					</Form.Item>
				</Col>
			</Row>
			<Form.Item label="Reason">
				<TextArea
					data-testid="draft-reason"
					rows={2}
					value={(value.reason as string) ?? ""}
					onChange={(e) => onChange({ ...value, reason: e.target.value || null })}
				/>
			</Form.Item>
		</Form>
	);
};

/**
 * Section-aware editable form for a scratchpad draft. Medical history reuses the frontend
 * detailFieldsMapping field renderers (same as CreateMedicalHistoryModal); other sections fall
 * back to a generic editable renderer driven by the draft shape.
 */
const ScratchpadDraftForm: React.FC<ScratchpadDraftFormProps> = ({ section, value, onChange }) => {
	const set = (path: (string | number)[], val: any) => onChange(setAt(value, path, val));

	if (section === "medical_history") {
		const historyType: string = value.history_type || "";
		const fields = detailFieldsMapping[historyType as keyof typeof detailFieldsMapping] || [];
		const details: Record<string, any> = value.details || {};

		return (
			<Form layout="vertical">
				<Form.Item label="History Type" required>
					<Select
						data-testid="draft-history_type"
						showSearch
						style={{ width: "100%" }}
						value={historyType || undefined}
						options={historyTypesOptions}
						placeholder="Select history type"
						onChange={(v) => set(["history_type"], v)}
					/>
				</Form.Item>

				<Form.Item label="Description">
					<TextArea data-testid="draft-description" rows={2} value={value.description || ""} onChange={(e) => set(["description"], e.target.value)} />
				</Form.Item>

				{fields.length > 0 && <Divider plain>Details</Divider>}

				<Row gutter={[16, 8]}>
					{fields.map((field) => (
						<Col xs={24} md={12} key={field.name}>
							<Form.Item label={field.label}>
								{field.type === "textarea" ? (
									<TextArea data-testid={`draft-${field.name}`} rows={2} value={details[field.name] ?? ""} onChange={(e) => set(["details", field.name], e.target.value)} />
								) : field.type === "number" ? (
									<InputNumber data-testid={`draft-${field.name}`} style={{ width: "100%" }} value={details[field.name] ?? null} onChange={(v) => set(["details", field.name], v)} />
								) : field.type === "boolean" ? (
									<Switch checked={!!details[field.name]} onChange={(checked) => set(["details", field.name], checked)} />
								) : field.type === "dropdown" && "options" in field ? (
									<Select data-testid={`draft-${field.name}`} showSearch style={{ width: "100%" }} value={details[field.name] ?? undefined} options={(field as any).options} placeholder="Select" onChange={(v) => set(["details", field.name], v)} />
								) : (
									<Input data-testid={`draft-${field.name}`} value={details[field.name] ?? ""} onChange={(e) => set(["details", field.name], e.target.value)} />
								)}
							</Form.Item>
						</Col>
					))}
				</Row>

				<Row gutter={[16, 8]}>
					<Col xs={24} md={12}>
						<Form.Item label="Date Identified">
							<Input data-testid="draft-date_identified" placeholder="YYYY-MM-DD" value={value.date_identified ?? ""} onChange={(e) => set(["date_identified"], e.target.value)} />
						</Form.Item>
					</Col>
					<Col xs={24} md={12}>
						<Form.Item label="Active">
							<Switch checked={value.is_active !== false} onChange={(checked) => set(["is_active"], checked)} />
						</Form.Item>
					</Col>
				</Row>
			</Form>
		);
	}

	// ── test_order ───────────────────────────────────────────────────────────────

	if (section === "test_order") {
		return <TestOrderDraftFields value={value} onChange={onChange} />;
	}

	// ── supplement_regimen ────────────────────────────────────────────────────────
	// Requires explicit handling: `items` is a nested array of objects which the
	// generic renderer cannot handle (arrays fall through to a plain Input).

	if (section === "supplement_regimen") {
		const items: Record<string, any>[] = Array.isArray(value.items) ? (value.items as Record<string, any>[]) : [];

		const updateItem = (idx: number, field: string, val: any) => {
			const newItems = items.map((item, i) => (i === idx ? { ...item, [field]: val } : item));
			onChange({ ...value, items: newItems });
		};

		const timingOptions = ["morning", "lunch", "dinner", "night", "other"].map((v) => ({
			value: v,
			label: prettify(v),
		}));

		const categoryOptions = [
			"ESSENTIAL",
			"STRONGLY RECOMMENDED",
			"BENEFICIAL",
			"CURRENT - CONTINUE",
			"CURRENT - DISCONTINUE",
		].map((v) => ({ value: v, label: v }));

		return (
			<Form layout="vertical">
				<Form.Item label="Regimen Name" required>
					<Input
						data-testid="draft-name"
						value={(value.name as string) ?? ""}
						onChange={(e) => set(["name"], e.target.value)}
					/>
				</Form.Item>
				<Form.Item label="Notes">
					<TextArea
						data-testid="draft-notes"
						rows={2}
						value={(value.notes as string) ?? ""}
						onChange={(e) => set(["notes"], e.target.value || null)}
					/>
				</Form.Item>
				{items.length > 0 && <Divider plain>Items</Divider>}
				{items.map((item, idx) => (
					<div
						key={idx}
						style={{
							marginBottom: 8,
							padding: 8,
							border: "1px solid var(--ant-color-border-secondary, #f0f0f0)",
							borderRadius: 4,
						}}
					>
						<Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
							Item {idx + 1}
						</Text>
						<Row gutter={[8, 4]}>
							<Col xs={24} md={12}>
								<Form.Item label="Product Name" required style={{ marginBottom: 4 }}>
									<Input
										data-testid={`draft-item-${idx}-product_name`}
										value={item.product_name ?? ""}
										onChange={(e) => updateItem(idx, "product_name", e.target.value)}
									/>
								</Form.Item>
							</Col>
							<Col xs={24} md={12}>
								<Form.Item label="Full Name" style={{ marginBottom: 4 }}>
									<Input
										data-testid={`draft-item-${idx}-full_name`}
										value={item.full_name ?? ""}
										onChange={(e) => updateItem(idx, "full_name", e.target.value)}
									/>
								</Form.Item>
							</Col>
							<Col xs={24} md={8}>
								<Form.Item label="Timing" style={{ marginBottom: 4 }}>
									<Select
										data-testid={`draft-item-${idx}-timing`}
										style={{ width: "100%" }}
										options={timingOptions}
										value={item.timing ?? "other"}
										onChange={(v: string) => updateItem(idx, "timing", v)}
									/>
								</Form.Item>
							</Col>
							<Col xs={24} md={8}>
								<Form.Item label="Category" style={{ marginBottom: 4 }}>
									<Select
										data-testid={`draft-item-${idx}-category`}
										style={{ width: "100%" }}
										options={categoryOptions}
										value={item.category ?? "BENEFICIAL"}
										onChange={(v: string) => updateItem(idx, "category", v)}
									/>
								</Form.Item>
							</Col>
							<Col xs={24} md={8}>
								<Form.Item label="Dosage" style={{ marginBottom: 4 }}>
									<Input
										data-testid={`draft-item-${idx}-dosage`}
										value={item.dosage ?? ""}
										onChange={(e) => updateItem(idx, "dosage", e.target.value || null)}
									/>
								</Form.Item>
							</Col>
						</Row>
					</div>
				))}
			</Form>
		);
	}

	// ── patient_note ──────────────────────────────────────────────────────────────
	// Requires explicit handling: `tags` is an array of strings which the generic
	// renderer would display as raw array text in a plain Input.

	if (section === "patient_note") {
		const tags: string[] = Array.isArray(value.tags) ? (value.tags as string[]) : [];
		return (
			<Form layout="vertical">
				<Form.Item label="Note" required>
					<TextArea
						data-testid="draft-body"
						rows={4}
						value={(value.body as string) ?? ""}
						onChange={(e) => set(["body"], e.target.value)}
					/>
				</Form.Item>
				<Row gutter={[16, 8]}>
					<Col xs={24} md={12}>
						<Form.Item label="Category">
							<Input
								data-testid="draft-category"
								placeholder="e.g. consultation, follow-up"
								value={(value.category as string) ?? ""}
								onChange={(e) => set(["category"], e.target.value || null)}
							/>
						</Form.Item>
					</Col>
					<Col xs={24} md={12}>
						<Form.Item label="Tags">
							<Select
								data-testid="draft-tags"
								mode="tags"
								style={{ width: "100%" }}
								value={tags}
								onChange={(v: string[]) => onChange({ ...value, tags: v.length ? v : null })}
								placeholder="Add tags"
							/>
						</Form.Item>
					</Col>
				</Row>
			</Form>
		);
	}

	// Generic renderer for the other sections (medications, vital_signs, referrals, personal_info).
	const renderField = (key: string, val: any, path: (string | number)[]): React.ReactNode => {
		if (typeof val === "boolean") {
			return (
				<Form.Item label={prettify(key)} key={key}>
					<Switch checked={val} onChange={(checked) => set(path, checked)} />
				</Form.Item>
			);
		}

		if (val && typeof val === "object" && !Array.isArray(val)) {
			return (
				<div key={key}>
					<Divider plain>{prettify(key)}</Divider>
					<Row gutter={[16, 8]}>
						{Object.entries(val).map(([subKey, subVal]) => (
							<Col xs={24} md={12} key={subKey}>
								{renderField(subKey, subVal, [...path, subKey])}
							</Col>
						))}
					</Row>
				</div>
			);
		}

		return (
			<Form.Item label={prettify(key)} key={key}>
				<Input data-testid={`draft-${key}`} value={val ?? ""} onChange={(e) => set(path, e.target.value)} />
			</Form.Item>
		);
	};

	const entries = Object.entries(value).filter(([key]) => key !== "instructions");

	if (entries.length === 0) {
		return (
			<Text type="secondary" style={{ fontStyle: "italic" }}>
				No fields were extracted — edit the note and regenerate.
			</Text>
		);
	}

	return <Form layout="vertical">{entries.map(([key, val]) => renderField(key, val, [key]))}</Form>;
};

export default ScratchpadDraftForm;
